import { useEffect, useRef } from 'react';
import { RevealOnScroll } from './MotionComponents';

const ROLE_LABEL = 'Miembro del Consejo Asesor';

const ADVISORS = [
  {
    name: 'Jorge Alonso',
    company: 'CEO · Evalue Innovación y Socio Director · Janus Management y Verum Asesores',
    desc: 'Consejero y docente en EDEM y UDIMA. Más de una década dirigiendo una consultora de innovación con presencia nacional.',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_precision-connect/artifacts/6kf99waw_image.png',
    bgSize: '142%',
    bgPosition: '88% 8%',
  },
  {
    name: 'Juanvi Tamarit',
    company: 'Fundador · NEA Strategic Advisors',
    desc: 'Ex-Director General y ex-Director Comercial en industria alimentaria. Más de 25 años en dirección de empresa familiar.',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_precision-connect/artifacts/gtojhhmc_image.png',
    objectPosition: 'center 8%',
  },
  {
    name: 'Esteban Bueno',
    company: 'Socio Director · Janus Management y Verum Asesores',
    desc: 'Especialista en Consejos de Administración, valoración de compañías y dirección financiera externa.',
    image: 'https://customer-assets-jt897jd0.emergentagent.net/job_precision-connect/artifacts/pf2e8ijo_image.png',
    objectPosition: 'center 35%',
  },
];

const PHOTO_FILTER = 'grayscale(1) contrast(1.03) brightness(0.95)';

function AdvisorCard({ advisor }) {
  return (
    <div className="flex items-start gap-6 bg-white/[0.04] rounded-sm p-6 h-full">
      <div className="relative flex-shrink-0 w-[88px] h-[88px]">
        <div className="w-full h-full rounded-full overflow-hidden ring-1 ring-[#C58C40]/40">
          {advisor.bgSize ? (
            <div
              role="img"
              aria-label={advisor.name}
              data-nosnippet
              className="w-full h-full select-none"
              style={{
                backgroundImage: `url(${advisor.image})`,
                backgroundSize: advisor.bgSize,
                backgroundPosition: advisor.bgPosition,
                backgroundRepeat: 'no-repeat',
                filter: PHOTO_FILTER,
              }}
            />
          ) : (
            <img
              src={advisor.image}
              alt={advisor.name}
              draggable={false}
              data-nosnippet
              className="w-full h-full object-cover select-none"
              style={{ filter: PHOTO_FILTER, objectPosition: advisor.objectPosition }}
            />
          )}
          {/* Soft vignette to fuse the circle with the dark background */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{ boxShadow: 'inset 0 0 14px 5px rgba(12,23,48,0.65)' }}
          />
        </div>
      </div>
      <div className="flex-1">
        <h3 className="text-white text-base font-medium tracking-wide leading-tight">
          {advisor.name}
        </h3>
        <p className="text-[#C58C40] text-[11px] font-medium tracking-[0.1em] uppercase mt-1 leading-tight">
          {ROLE_LABEL}
        </p>
        <p className="text-[#8A9BB1] text-xs tracking-wide mt-1 leading-snug">
          {advisor.company}
        </p>
        <p className="text-[#8A9BB1]/75 text-sm leading-relaxed mt-3">
          {advisor.desc}
        </p>
      </div>
    </div>
  );
}

function InfiniteCarousel() {
  const scrollRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });

  // Triplicate the set so the middle copy can loop seamlessly in both directions.
  const cards = [...ADVISORS, ...ADVISORS, ...ADVISORS];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Position at the start of the middle set once laid out.
    const init = () => {
      const oneSet = el.scrollWidth / 3;
      if (oneSet > 0) el.scrollLeft = oneSet;
    };
    const id = requestAnimationFrame(init);

    const handleScroll = () => {
      const oneSet = el.scrollWidth / 3;
      if (oneSet <= 0) return;
      if (el.scrollLeft < oneSet * 0.5) {
        el.scrollLeft += oneSet;
      } else if (el.scrollLeft > oneSet * 1.5) {
        el.scrollLeft -= oneSet;
      }
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', init);
    return () => {
      cancelAnimationFrame(id);
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', init);
    };
  }, []);

  // Mouse drag (native touch handles finger drag).
  const onMouseDown = (e) => {
    const el = scrollRef.current;
    drag.current = { down: true, startX: e.pageX, startScroll: el.scrollLeft };
    el.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!drag.current.down) return;
    e.preventDefault();
    const el = scrollRef.current;
    el.scrollLeft = drag.current.startScroll - (e.pageX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.down = false;
    const el = scrollRef.current;
    if (el) el.style.cursor = 'grab';
  };

  return (
    <div className="relative -mx-6 md:-mx-12">
      {/* Edge fades to hint continuity */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 z-10 bg-gradient-to-r from-[#0C1730] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 z-10 bg-gradient-to-l from-[#0C1730] to-transparent" />

      <div
        ref={scrollRef}
        data-testid="consejo-carousel"
        className="flex items-stretch gap-5 overflow-x-auto no-scrollbar px-6 snap-x snap-mandatory cursor-grab"
        style={{ scrollBehavior: 'auto' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        {cards.map((advisor, i) => (
          <div
            key={`${advisor.name}-${i}`}
            data-testid={`advisor-card-${i}`}
            className="snap-start flex-shrink-0 w-[85vw] max-w-[440px]"
          >
            <AdvisorCard advisor={advisor} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ConsejoAsesor() {
  return (
    <section
      data-testid="consejo-asesor-section"
      className="bg-[#0C1730] py-16 relative overflow-hidden"
    >
      <div className="grain absolute inset-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <RevealOnScroll className="mb-3">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase block">
            Quiénes nos respaldan
          </span>
        </RevealOnScroll>

        <RevealOnScroll delay={0.1} className="mb-10">
          <h2
            data-testid="consejo-asesor-headline"
            className="text-xl md:text-2xl font-normal tracking-[0.01em] leading-snug text-white"
          >
            Nuestro Consejo Asesor
          </h2>
          <p className="text-[#8A9BB1] text-sm leading-relaxed max-w-[520px] mt-3">
            Profesionales de referencia que validan nuestro modelo y garantizan la excelencia del ecosistema.
          </p>
        </RevealOnScroll>

        <RevealOnScroll delay={0.2}>
          {/* Desktop: three cards fully visible, centered, no scroll */}
          <div className="hidden md:flex items-stretch justify-center gap-5">
            {ADVISORS.map((advisor, i) => (
              <div key={advisor.name} data-testid={`advisor-card-desktop-${i}`} className="flex-1 max-w-[440px]">
                <AdvisorCard advisor={advisor} />
              </div>
            ))}
          </div>
          {/* Mobile / narrow: infinite drag carousel */}
          <div className="md:hidden">
            <InfiniteCarousel />
          </div>
        </RevealOnScroll>
      </div>
    </section>
  );
}
